/*
  header-js-v12-faster-close.js

  V12 retains the current opening animation, overlay synchronisation, hover
  protection and mobile drawer behaviour.

  Desktop closing refinements:
  - Mega-menu closing animation reduced from 289ms to 200ms.
  - Standard desktop dropdown closing transform reduced from 347ms to 220ms.
  - Standard dropdown closing opacity reduced from 174ms to 120ms.
  - Hover-leave grace period reduced from 180ms to 90ms.
  - Overlay closing duration now matches the menu being closed.
  - Opening timings are unchanged.
*/

class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      // Cache layout computations to reduce reflow
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      // Batch style updates
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Desktop hover handling with a short close grace period.
    this.hoverTimer = null;
    this.isOpening = false;
    this.pendingHoverClose = false;
    this.transitionSequence = 0;

    // Fixed page overlay rendered by the same Header section. Keeping the
    // lookup local prevents us from touching drawer/cart overlays elsewhere.
    this.headerSection = this.closest('.shopify-section');
    this.overlayElement = this.headerSection
      ? this.headerSection.querySelector('.fixed-overlay.z-10')
      : null;

    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener);
    this.addEventListener('mouseleave', this.detectHoverListener);

    if (this.contentElement) {
      this.contentElement.addEventListener('mouseenter', () => {
        if (this.trigger !== 'hover') return;
        window.clearTimeout(this.hoverTimer);
        this.pendingHoverClose = false;
        this.open = true;
      });

      this.contentElement.addEventListener('mouseleave', () => {
        if (this.trigger !== 'hover') return;
        this.scheduleHoverClose();
      });
    }
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (this.hasAttribute('trigger')) {
      return this.getAttribute('trigger');
    } else {
      return 'click';
    }
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  getOverlayAnimationConfig(opening) {
    const isMegaMenu = this.matches('details[is="details-mega"]');

    if (isMegaMenu) {
      return {
        duration: FoxTheme.config.motionReduced ? 0 : opening ? 289 : 200,
        easing: 'cubic-bezier(0.39, 0.575, 0.565, 1)',
      };
    }

    return {
      duration: FoxTheme.config.motionReduced ? 0 : opening ? 347 : 220,
      easing: 'cubic-bezier(0.7, 0, 0.2, 1)',
    };
  }

  animatePageOverlay(opening) {
    if (!this.overlayElement) return Promise.resolve();

    const overlay = this.overlayElement;
    const { duration, easing } = this.getOverlayAnimationConfig(opening);

    if (overlay.__femmeMenuOverlayAnimation) {
      overlay.__femmeMenuOverlayAnimation.cancel();
      overlay.__femmeMenuOverlayAnimation = null;
    }

    // Disable the theme's own overlay transition so there is only one timing
    // source, but do NOT lock opacity with !important: the Web Animation must
    // be allowed to interpolate it.
    overlay.style.setProperty('transition', 'none', 'important');
    overlay.style.setProperty('animation', 'none', 'important');
    overlay.style.setProperty('visibility', 'visible', 'important');
    overlay.style.setProperty('will-change', 'opacity', 'important');

    const computedOpacity = Number.parseFloat(
      window.getComputedStyle(overlay).opacity
    );

    // A brand-new opening always starts at true zero. On closing/reversing,
    // preserve the current visual opacity so the fade remains continuous.
    const fromOpacity = opening
      ? 0
      : Number.isFinite(computedOpacity)
        ? computedOpacity
        : 1;
    const toOpacity = opening ? 1 : 0;

    // Inline normal-priority opacity establishes the first frame without
    // blocking the Web Animation cascade.
    overlay.style.opacity = String(fromOpacity);

    const animation = overlay.animate(
      [
        { opacity: fromOpacity },
        { opacity: toOpacity },
      ],
      {
        duration,
        easing,
        fill: 'forwards',
      }
    );

    overlay.__femmeMenuOverlayAnimation = animation;

    return animation.finished
      .catch(() => undefined)
      .then(() => {
        if (overlay.__femmeMenuOverlayAnimation !== animation) return;

        // Commit the final visual state before cancelling the animation.
        overlay.style.opacity = String(toOpacity);
        animation.cancel();
        overlay.__femmeMenuOverlayAnimation = null;
      });
  }

  resetPageOverlayStyles() {
    if (!this.overlayElement) return;

    const overlay = this.overlayElement;

    if (overlay.__femmeMenuOverlayAnimation) {
      overlay.__femmeMenuOverlayAnimation.cancel();
      overlay.__femmeMenuOverlayAnimation = null;
    }

    overlay.style.removeProperty('opacity');
    overlay.style.removeProperty('visibility');
    overlay.style.removeProperty('transition');
    overlay.style.removeProperty('animation');
    overlay.style.removeProperty('will-change');
  }

  async transition(value) {
    const transitionSequence = ++this.transitionSequence;

    if (value) {
      this.isOpening = true;
      this.pendingHoverClose = false;

      // Increment the lock count for dropdowns.
      const previousDropdownCount = clearDropdownCount.get(DetailsDropdown);
      clearDropdownCount.set(DetailsDropdown, previousDropdownCount + 1);

      // Set menu attributes first.
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      this.contentElement.setAttribute('open', '');

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Start the overlay before the body class can paint it at its final
      // opacity. The menu animation is started immediately afterwards in the
      // same JavaScript turn, so both begin on the same rendered frame.
      const overlayPromise =
        previousDropdownCount === 0
          ? this.animatePageOverlay(true)
          : Promise.resolve();

      document.body.classList.add(this.classes.bodyClass);

      const menuPromise = this.showWithTransition();

      await Promise.all([menuPromise, overlayPromise]);

      // Ignore an older animation completing after the state has changed.
      if (transitionSequence !== this.transitionSequence || !this.open) {
        return;
      }

      this.isOpening = false;

      // If the pointer left while the menu was still opening, only decide
      // whether to close now that the panel has reached its final position.
      if (this.pendingHoverClose) {
        this.pendingHoverClose = false;
        this.scheduleHoverClose();
      }

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      this.isOpening = false;
      this.pendingHoverClose = false;

      // Decrement the lock count for dropdowns.
      const nextDropdownCount = Math.max(
        0,
        clearDropdownCount.get(DetailsDropdown) - 1
      );
      clearDropdownCount.set(DetailsDropdown, nextDropdownCount);

      // Remove summary state and event listeners. Keep the content open while
      // the panel itself is animating closed so its contents travel with it.
      this.summaryElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Fade the overlay out alongside the last menu only.
      const overlayPromise =
        nextDropdownCount === 0
          ? this.animatePageOverlay(false)
          : Promise.resolve();

      const menuPromise = this.hideWithTransition();

      await Promise.all([menuPromise, overlayPromise]);

      if (nextDropdownCount === 0 && clearDropdownCount.get(DetailsDropdown) === 0) {
        document.body.classList.remove(this.classes.bodyClass);
        this.resetPageOverlayStyles();
      } else {
        document.body.classList.add(this.classes.bodyClass);
      }

      // If the pointer re-entered and triggered a new opening animation while
      // this close was still running, this close is stale and must not remove
      // any open-state attributes.
      if (transitionSequence !== this.transitionSequence || this.open) {
        return;
      }

      this.contentElement.removeAttribute('open');

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.347,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.058,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.347,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.12,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.22,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  scheduleHoverClose() {
    window.clearTimeout(this.hoverTimer);

    // Do not let the menu reverse direction while the user is moving from the
    // header into a panel that has not reached its final position yet.
    if (this.isOpening) {
      this.pendingHoverClose = true;
      return;
    }

    this.hoverTimer = window.setTimeout(() => {
      const pointerStillInside =
        this.matches(':hover') ||
        (this.contentElement && this.contentElement.matches(':hover'));

      if (!pointerStillInside) {
        this.open = false;
      }
    }, 90);
  }

  detectHover(event) {
    if (this.trigger !== 'hover') return;

    window.clearTimeout(this.hoverTimer);

    if (event.type === 'mouseenter') {
      this.pendingHoverClose = false;
      this.open = true;
      return;
    }

    this.scheduleHoverClose();
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.289,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.2,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get menuItems() {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.menu-drawer__menus:not(.active) > li');
    }
    return this._menuItems;
  }

  prepareToShow() {
    super.prepareToShow();
    setTimeout(() => {
      this.animateMenuItems();
    }, 300);
  }

  animateMenuItems() {
    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger(0.1),
      }
    ).finished.then(() => {
      this.menuItems.forEach((item) => item.removeAttribute('style'));
    });
  }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }
}
customElements.define('menu-product-list', MenuProductList);
